import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../lib/auth/enterpriseAuth';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  LayoutDashboard,
  Code2,
  Key,
  Settings,
  FileText,
  Webhook,
  Menu,
  X,
  ChevronDown,
  Bell,
  Search,
  LogOut,
  Building2,
  User,
  Shield,
  HelpCircle,
  ClipboardCheck,
  SlidersHorizontal
} from 'lucide-react';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const navigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    permissions: ['analytics:view']
  },
  {
    name: 'API Keys',
    href: '/api-keys',
    icon: Key,
    permissions: ['api-keys:manage']
  },
  {
    name: 'API Reference',
    href: '/api-reference',
    icon: Code2,
    permissions: ['identity:read', 'aml:read', 'fraud:read']
  },
  {
    name: 'Integration Guides',
    href: '/integration-guides',
    icon: FileText,
    permissions: ['identity:read', 'aml:read', 'fraud:read']
  },
  {
    name: 'Webhooks',
    href: '/webhooks',
    icon: Webhook,
    permissions: ['webhooks:manage']
  },
  {
    name: 'Hosted Onboarding',
    href: '/hosted-onboarding',
    icon: ClipboardCheck,
    permissions: ['identity:read']
  },
  {
    name: 'Fayda Readiness',
    href: '/developers/fayda',
    icon: Shield,
    permissions: ['identity:read']
  },
  {
    name: 'Decision Sandbox',
    href: '/decision-control',
    icon: SlidersHorizontal,
    permissions: ['identity:read']
  },
  {
    name: 'Documentation',
    href: '/docs',
    icon: FileText,
    permissions: ['identity:read']
  }
];

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, organization, subscription, signOut, hasPermission } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const filteredNavigation = navigation.filter(item => 
    item.permissions.some(permission => hasPermission(permission))
  );

  const handleSignOut = () => {
    signOut();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={{
          x: sidebarOpen ? 0 : '-100%'
        }}
        className="fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Code2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Wardya</h1>
                <p className="text-xs text-gray-500">Developer Portal</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Organization Info */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-blue-500 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {organization?.name}
                </p>
                <div className="flex items-center space-x-2">
                  <Badge 
                    className={`text-xs capitalize ${
                      subscription?.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                      subscription?.plan === 'professional' ? 'bg-blue-100 text-blue-800' :
                      subscription?.plan === 'starter' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {subscription?.plan}
                  </Badge>
                  {subscription?.status === 'trialing' && (
                    <Badge className="bg-orange-100 text-orange-800 text-xs">
                      Trial
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
              >
                <item.icon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                {item.name}
              </a>
            ))}
          </nav>

          {/* Usage Stats */}
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">API Usage</span>
                <span className="text-xs text-gray-500">
                  {subscription?.usage.monthlyLimit === -1 
                    ? 'Unlimited' 
                    : `${subscription?.usage.apiCalls.toLocaleString()} / ${subscription?.usage.monthlyLimit.toLocaleString()}`
                  }
                </span>
              </div>
              {subscription?.usage.monthlyLimit !== -1 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ 
                      width: `${Math.min((subscription?.usage.apiCalls || 0) / (subscription?.usage.monthlyLimit || 1) * 100, 100)}%` 
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Help & Support */}
          <div className="px-6 py-4 border-t border-gray-200">
            <Button variant="ghost" className="w-full justify-start" size="sm">
              <HelpCircle className="mr-3 h-4 w-4" />
              Help & Support
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Top bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              
              {/* Search */}
              <div className="hidden sm:block relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search APIs, docs..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-64"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <Button variant="ghost" size="sm" className="relative">
                <Bell className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-xs text-white font-medium">2</span>
                </span>
              </Button>

              {/* Profile dropdown */}
              <div className="relative">
                <Button
                  variant="ghost"
                  className="flex items-center space-x-3 hover:bg-gray-100 pr-2"
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.firstName.charAt(0)}{user?.lastName.charAt(0)}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </Button>

                {/* Dropdown menu */}
                <AnimatePresence>
                  {profileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                    >
                      <div className="py-2">
                        <a
                          href="/profile"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <User className="mr-3 h-4 w-4" />
                          Profile
                        </a>
                        <a
                          href="/settings"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Settings className="mr-3 h-4 w-4" />
                          Settings
                        </a>
                        <a
                          href="/security"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Shield className="mr-3 h-4 w-4" />
                          Security
                        </a>
                        <hr className="my-2" />
                        <button
                          onClick={handleSignOut}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <LogOut className="mr-3 h-4 w-4" />
                          Sign out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
